/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';

import api from 'services/api';
import { getExpertPhoto } from 'components/organisms/central_ia_library/expert_photos';

import { Container } from './styles';

interface Category {
  slug: string;
  name: string;
}

interface ExpertPrompt {
  slug: string;
  title: string;
  summary: string;
  topics: string[];
}

interface ExpertProfile {
  slug: string;
  name: string;
  role: string;
  bio: string;
  photoUrl: string;
  instagram: string;
  promptsCount: number;
}

interface CentralIaExpertProps {
  slug: string;
  categories: Category[];
  onBack: () => void;
  onOpenPrompt: (promptSlug: string) => void;
  onUsePrompt: (body: string, providerId?: string, model?: string) => void;
  background?: string;
}

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeTopics = (topics: any): string[] =>
  Array.isArray(topics)
    ? topics.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (topic: any) => (typeof topic === 'string' ? topic : topic?.slug ?? topic?.name ?? ''),
      )
    : [];

const CentralIaExpert: React.FC<CentralIaExpertProps> = ({
  slug,
  categories,
  onBack,
  onOpenPrompt,
  onUsePrompt,
  background,
}) => {
  const [expert, setExpert] = useState<ExpertProfile | null>(null);
  const [prompts, setPrompts] = useState<ExpertPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [usingSlug, setUsingSlug] = useState<string | null>(null);
  const [copyBusySlug, setCopyBusySlug] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    api
      .get(`/chatbots/central_ia/biblioteca/especialistas/${slug}`)
      .then((response) => {
        if (!active) return;
        const data = response.data || {};
        const rawExpert = data.expert || {};
        setExpert({
          slug: rawExpert.slug,
          name: rawExpert.name,
          role: rawExpert.role ?? '',
          bio: rawExpert.bio ?? '',
          photoUrl: rawExpert.photo_url ?? '',
          instagram: rawExpert.instagram_handle ?? '',
          promptsCount: rawExpert.prompts_count ?? 0,
        });
        setPrompts(
          Array.isArray(data.prompts)
            ? data.prompts.map(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (prompt: any) => ({
                  slug: prompt.slug,
                  title: prompt.title,
                  summary: prompt.summary ?? '',
                  topics: normalizeTopics(prompt.topics),
                }),
              )
            : [],
        );
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!copiedSlug) return undefined;
    const timeoutId = window.setTimeout(() => setCopiedSlug(null), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [copiedSlug]);

  const categoryNameBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((category) => {
      map[category.slug] = category.name;
    });
    return map;
  }, [categories]);

  const filteredPrompts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return prompts.filter((prompt) => {
      const matchesSearch =
        !term ||
        prompt.title.toLowerCase().includes(term) ||
        prompt.summary.toLowerCase().includes(term);
      const matchesCategory = !activeCategory || prompt.topics.includes(activeCategory);
      return matchesSearch && matchesCategory;
    });
  }, [prompts, search, activeCategory]);

  const fetchPromptBody = async (promptSlug: string): Promise<string> => {
    const response = await api.get(`/chatbots/central_ia/biblioteca/prompts/${promptSlug}`);
    return response.data?.body ?? '';
  };

  const handleUse = async (promptSlug: string) => {
    setUsingSlug(promptSlug);
    try {
      const body = await fetchPromptBody(promptSlug);
      if (body) onUsePrompt(body);
    } catch {
      // ignora — falha ao carregar o prompt.
    } finally {
      setUsingSlug(null);
    }
  };

  const handleCopy = async (promptSlug: string) => {
    setCopyBusySlug(promptSlug);
    try {
      const body = await fetchPromptBody(promptSlug);
      await navigator.clipboard.writeText(body);
      setCopiedSlug(promptSlug);
    } catch {
      // clipboard indisponivel ou falha — ignora.
    } finally {
      setCopyBusySlug(null);
    }
  };

  const firstName = expert?.name?.split(' ')[0] || 'especialista';

  return (
    <Container style={background ? { background } : undefined}>
      <div className="expert-inner">
        <button type="button" className="back-button" onClick={onBack}>
          <i className="material-icons">arrow_back</i>
          Todos os professores
        </button>

        {loading ? (
          <div className="library-empty">Carregando...</div>
        ) : error || !expert ? (
          <div className="library-empty">Não foi possível carregar o especialista.</div>
        ) : (
          <>
            <div className="profile-card">
              <div className="profile-head">
                {getExpertPhoto(expert.photoUrl) ? (
                  <img
                    className="expert-photo"
                    src={getExpertPhoto(expert.photoUrl)}
                    alt={expert.name}
                  />
                ) : (
                  <div className="expert-photo">{getInitials(expert.name)}</div>
                )}
                <div>
                  <div className="expert-name">{expert.name}</div>
                  {expert.role && <div className="expert-role">{expert.role}</div>}
                  {expert.bio && <p className="expert-bio">{expert.bio}</p>}
                  <div className="profile-meta">
                    <span className="prompts-badge">
                      <i className="material-icons">auto_awesome</i>
                      {expert.promptsCount} prompts
                    </span>
                    {expert.instagram && (
                      <a
                        className="instagram-badge"
                        href={`https://www.instagram.com/${expert.instagram.replace(/^@/, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="material-icons">alternate_email</i>
                        {expert.instagram.replace(/^@/, '')}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="expert-search">
              <i className="material-icons">search</i>
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={`Buscar entre os prompts de ${firstName}...`}
              />
            </div>

            {categories.length > 0 && (
              <div className="expert-categories">
                {categories.map((category) => (
                  <button
                    key={category.slug}
                    type="button"
                    className={`category-chip ${activeCategory === category.slug ? 'active' : ''}`}
                    onClick={() =>
                      setActiveCategory((current) =>
                        current === category.slug ? null : category.slug,
                      )
                    }
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            )}

            {filteredPrompts.length === 0 ? (
              <div className="library-empty">Nenhum prompt encontrado.</div>
            ) : (
              <div className="prompts-grid">
                {filteredPrompts.map((prompt) => (
                  <div key={prompt.slug} className="prompt-card">
                    <div className="prompt-card-head">
                      <div className="prompt-title">{prompt.title}</div>
                      {prompt.topics[0] && (
                        <span className="topic-chip">
                          {categoryNameBySlug[prompt.topics[0]] ?? prompt.topics[0]}
                        </span>
                      )}
                    </div>
                    {prompt.summary && <p className="prompt-summary">{prompt.summary}</p>}
                    <div className="prompt-card-foot">
                      <button
                        type="button"
                        className="use-button"
                        onClick={() => handleUse(prompt.slug)}
                        disabled={usingSlug === prompt.slug}
                      >
                        <i className="material-icons">auto_awesome</i>
                        {usingSlug === prompt.slug ? 'Abrindo...' : 'Usar no chat'}
                      </button>
                      <button
                        type="button"
                        className="copy-button"
                        onClick={() => handleCopy(prompt.slug)}
                        disabled={copyBusySlug === prompt.slug}
                        title="Copiar prompt"
                        aria-label="Copiar prompt"
                      >
                        <i className="material-icons">
                          {copiedSlug === prompt.slug ? 'check' : 'content_copy'}
                        </i>
                      </button>
                      <button
                        type="button"
                        className="full-button"
                        onClick={() => onOpenPrompt(prompt.slug)}
                      >
                        Ver completo
                        <i className="material-icons">chevron_right</i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default CentralIaExpert;
